import { alpha, createTheme } from '@mui/material/styles';

// The client's colour table (Vanja Veric, 2026-10-09), by name.
interface BrandColours {
  ivory: string;
  black: string;
  pink: string;
  pinkMid: string;
  pinkHighlight: string;
  magenta: string;
  border: string;
  grey: string;
  white: string;
  mutedBlueGray: string;
}

declare module '@mui/material/styles' {
  interface TypeBackground {
    dark: string;
  }

  // text.primary and text.secondary are the text colours of the surface you
  // are on: the page, or a dark panel inside <OnDark>. These two do not move
  // with the surface. onDark is for text on a dark fill or over a picture;
  // onLight is for text on a light or bright fill.
  interface TypeText {
    onDark: string;
    onLight: string;
  }

  interface PaletteColor {
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    500?: string;
    600?: string;
    700?: string;
  }

  interface Color {
    main?: string;
  }

  interface SimplePaletteColorOptions {
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    500?: string;
    600?: string;
    700?: string;
  }

  interface Palette {
    grey: Color;
    error: PaletteColor;
    warning: PaletteColor;
    success: PaletteColor;
    info: PaletteColor;
    brand: BrandColours;
    // The soft ring a pink button gains under the mouse and with focus.
    hoverRing: string;
  }

  interface PaletteOptions {
    brand?: BrandColours;
    hoverRing?: string;
  }

  interface TypographyVariants {
    h7: React.CSSProperties;
    mainTitles: React.CSSProperties;
    paragraph: React.CSSProperties;
    paragraphSmall: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    h7: React.CSSProperties;
    mainTitles?: React.CSSProperties;
    paragraph?: React.CSSProperties;
    paragraphSmall?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h7: true;
    mainTitles: true;
    paragraph: true;
    paragraphSmall: true;
  }
}

const font = {
  size: {
    xss: '0.75rem',
    xs: '0.875rem',
    sm: '1rem',
    md: '1.25rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem',
    xxxl: '4rem',
  },
  lineHeight: { xs: '1.313rem', sm: '1.5rem' },
  weight: {
    thin: 100,
    extraLight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
    black: 900,
  },
};

// The client's colour table, exactly as sent (Vanja Veric, 2026-10-09). Her
// colours are written here; the rest of the app refers to them by name
// (palette.brand) or, mostly, by the role they play (text.primary,
// primary.main, divider and so on, below).
//
//   ivory          Warm ivory background             "Keep"
//   black          Primary black                     "Headlines, CODEWORD wordmark"
//   pink           Main hot pink / CTA               "Search button, active nav, links"
//   pinkMid        Pink gradient mid-tone            "Logo + headline gradient"
//   pinkHighlight  Pink highlight                    "Bright end of gradients"
//   magenta        Deep magenta (NEW / optional)     "Shadow/depth inside the C logo only"
//   border         Soft warm border                  "Search/header borders"
//   grey           Secondary gray text               "Secondary copy"
//   white          White                             "Cards/icons as needed"
//
// Added from RJ's revised homepage font guide (20 Oct), its third colour:
//   mutedBlueGray  Muted blue-gray                   The homepage's description
//                                                    lines, the search
//                                                    placeholder and the footer
// It measures 3.52:1 on the ivory and 3.64:1 on white, under the 4.5:1 for
// normal text: used as given on Joey's decision (Q6, 20 Oct), recorded as a
// known gap, as the pink links were.
const brand: BrandColours = {
  ivory: '#FDFBF6',
  black: '#050506',
  pink: '#FB0674',
  pinkMid: '#ED357A',
  pinkHighlight: '#FC5E9A',
  magenta: '#B60958',
  border: '#DED8CE',
  grey: '#66666D',
  white: '#FFFFFF',
  mutedBlueGray: '#7B86A2',
};

const theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Flat warm ivory. "The ivory should stay warm rather than pure
          // white" (Vanja, 2026-10-09).
          backgroundColor: brand.ivory,
          height: '100vh',
          overflowY: 'scroll',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        },
      },
    },
    // Menu popups are the one surface where this app's dark-text/light-surface
    // split goes wrong. Paper paints every popup with background.paper AND
    // color: text.primary, and MenuItem sets no colour of its own. Since the
    // 2026-10 rebrand the page's own text is dark, so on the page this override
    // only restates what Paper would do. It still does its original job inside
    // a dark panel (onDarkTheme, below), where background.paper is #f9f9f9 and
    // text.primary is #ffffff: white inherited onto near-white, 1.05:1, invisible.
    // The selected row is no better, 1.20:1, because its only marker is an 8%
    // primary tint.
    //
    // Scoped to MuiMenu rather than MuiPaper on purpose: menu popups are the set
    // that has the defect, and every Card, Dialog and Accordion in the app is
    // Paper too. Not fixable by palette.mode either, since an explicit
    // background.paper wins the deepmerge over any mode default.
    //
    // grey[700] measures 17.94:1 unselected, 15.77:1 selected, 16.43:1 hovered
    // and 14.78:1 selected+hovered on that #f9f9f9 paper (18.88, 16.58, 17.29
    // and 15.52 on the page's white paper), all well clear of the 4.5:1 WCAG
    // 2.2 AA floor for normal text that the Accessibility Statement commits to.
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          color: theme.palette.grey[700],
        }),
      },
    },
    // A placeholder is currentColor at an opacity MUI picks from palette.mode:
    // 0.42 for 'light', which is what this theme resolves to. On the admin rate
    // grid the placeholder is not decorative - it carries the INHERITED rate, a
    // real number an admin reads. At 0.42 the page's text colour measures
    // 2.94:1 on the ivory page, under 1.4.3's 4.5:1; at 0.55 it measures 4.53:1
    // (4.57:1 on a white card). The value was first set for the dark page,
    // where white at 0.42 was 3.32:1 on the #030A8C glow and 0.55 was 4.94:1.
    //
    // Theme-level is safe here in a way the outlined notch is not. Opacity blends
    // currentColor toward whatever is behind it, so raising it moves every
    // placeholder in the app closer to its own input's text colour: contrast can
    // only go up, on a dark page or a white inset alike. That is not true of a
    // colour, which is why the notch fix is component-scoped instead.
    //
    // The distinction this must NOT destroy: a blank input showing the inherited
    // 100 and a typed input showing 100 are positionally identical, so opacity is
    // the only signal that one is inherited. At 0.55 the gap is still 4.53:1
    // against the typed value's 19.70:1. Pushing higher starts erasing meaning,
    // and the answer then is a non-colour cue, not more contrast.
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': { opacity: 0.55 },
          '&::-webkit-input-placeholder': { opacity: 0.55 },
          '&::-moz-placeholder': { opacity: 0.55 },
        },
      },
    },
  },
  palette: {
    primary: {
      // The action colour. "The most important brand color is #FB0674 hot
      // pink" (Vanja, 2026-10-09).
      main: brand.pink,
      // The bright pink: gradients and lit highlights.
      light: brand.pinkHighlight,
      // MUI's own contained buttons take this token as their fill under the
      // mouse (the admin's Save, Search, Apply and Load more). It is the
      // client's mid pink, so those buttons change fill on hover as they
      // always have (Joey, 2026-10-13). The change is one of hue only: the
      // two pinks are 1.00:1 apart in contrast, and white text is 3.90:1 on
      // both. The app's own pink buttons do not read this token: they keep
      // the hot pink under the mouse and gain a soft ring (hoverRing, below).
      // Its one other reader is a disabled icon on My information, which MUI
      // paints in its own disabled colour.
      dark: brand.pinkMid,
      // Text on the action colour. White on #FB0674 measures 3.90:1, under
      // the 4.5:1 floor for normal text. Built as given on Joey's instruction
      // (2026-10-12); the question is open with Vanja. This is the one line
      // to change if her answer is black (5.22:1).
      contrastText: brand.white,
    },
    secondary: {
      // Selected, hover and focus accents: the same hot pink. "I'd use that
      // consistently for the search button, active navigation state, selected
      // filters, links/hover states, and other small interactions."
      main: brand.pink,
      // NOT a brand accent: the error tone on the sign-in forms. Unchanged.
      light: '#F26463',
      contrastText: brand.white,
    },
    grey: {
      100: '#FFFFFF',
      200: '#FBFBFB',
      300: '#F1F1F1',
      400: '#CBCBCA',
      500: '#767676',
      600: '#575757',
      700: '#111111',
    },
    error: {
      100: '#FFDEDE',
      200: '#F0B3B3',
      300: '#EAB8C8',
      400: '#EB6A6A',
      500: '#DD444A',
      600: '#DB0000',
      main: '#DD444A',
    },
    warning: {
      100: '#FFF9DB',
      200: '#FFF2B2',
      300: '#FFE69A',
      400: '#FFD85A',
      500: '#FFC107',
      600: '#FFA000',
      main: '#FFC107',
    },
    success: {
      100: '#E8F5E9',
      200: '#C8E6C9',
      300: '#A5D6A7',
      400: '#81C784',
      500: '#66BB6A',
      600: '#388E3C',
      main: '#4CAF50',
    },
    info: {
      100: '#E3F2FD',
      200: '#BBDEFB',
      300: '#90CAF9',
      400: '#64B5F6',
      500: '#42A5F5',
      600: '#1976D2',
      main: '#2196F3',
    },
    background: {
      // The page.
      default: brand.ivory,
      // Cards and popups.
      paper: brand.white,
      // The fill of a panel that stays dark (see OnDark.tsx).
      dark: brand.black,
    },
    // Lines on the page. 1.37:1 against the ivory: decoration, as the client
    // specified it. A line that has to show where a control is (a field's
    // outline, a checkbox) uses grey[500] instead, 4.39:1.
    divider: brand.border,
    text: {
      // Text on the page: 19.70:1 on the ivory.
      primary: brand.black,
      // Muted text on the page: 5.51:1 on the ivory, 5.70:1 on a white card.
      secondary: brand.grey,
      onDark: brand.white,
      onLight: brand.black,
    },
    brand,
    // A pink button keeps its fill under the mouse and gains a soft ring of
    // the same pink (Joey, 2026-10-12): `0 0 0 4px` of this. At 25 percent it
    // measures 1.51:1 against the ivory page and 1.52:1 against a white card:
    // soft, and plain to see.
    hoverRing: alpha(brand.pink, 0.25),
  },
  typography: {
    // RJ's font guide (20 Oct): "Inter, use for all website text and
    // controls" (Joey, Q5: the whole site).
    fontFamily: "'Inter', sans-serif",
    h1: {
      fontWeight: 700,
      fontSize: '4rem',
      lineHeight: 1.5,
    },
    h2: {
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.5,
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.5rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: 1.3,
    },
    h7: {
      fontWeight: font.weight.bold,
      fontSize: font.size.xl,
      lineHeight: '2.25rem',
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    subtitle1: {
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.75,
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 500,
      fontSize: '1rem',
      textTransform: 'uppercase',
    },
    caption: {
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    overline: {
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 2.66,
      textTransform: 'uppercase',
    },
    // Custom Typography
    mainTitles: {
      fontSize: font.size.lg,
      fontWeight: font.weight.bold,
      lineHeight: '3rem',
    },
    paragraph: {
      fontSize: font.size.sm,
      fontWeight: font.weight.normal,
      lineHeight: font.lineHeight.sm,
    },
    paragraphSmall: {
      fontSize: font.size.xs,
      fontWeight: font.weight.normal,
      lineHeight: font.lineHeight.xs,
    },
  },
});

// The values a dark panel keeps whatever the page's are (see OnDark.tsx):
// laid over the theme above the panel, they are the whole of the difference
// between a dark panel and the page.
//
// The first five values are the ones the whole site had before the 2026-10
// rebrand, when every surface was dark. Keeping them is what keeps the inside
// of each dark panel as it was: white text; #BDBDBD muted text (7:1 or better
// on every dark fill); and, for MUI's own parts, the near-white popup paper,
// the white default surface and the 12 percent black divider they had before.
// The sixth is the hover ring. Everything else inside a dark panel, the pink
// accents included, is the page's.
export const onDarkOverlay = {
  palette: {
    text: {
      primary: '#ffffff',
      secondary: '#BDBDBD',
    },
    background: {
      default: '#ffffff',
      paper: '#f9f9f9',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    // The same ring, stronger. At the page's 25 percent it is 1.24:1 against
    // a dark panel and cannot be seen; at 60 percent it is 2.39:1 (2.33:1 on
    // a dialog's #111111).
    hoverRing: alpha(brand.pink, 0.6),
  },
};

// The page theme with the dark panel values laid over it.
export const onDarkTheme = createTheme(theme, onDarkOverlay);

export default theme;
